// Shared "directory" table look — the light, rounded style used by the
// Employees and Selected Candidates tables. Import these tokens so every list
// table across the app renders identically instead of each page rolling its own
// (dark headers, bankTable CSS, etc.).
import { alpha, styled } from '@mui/material/styles';

// Header cell
export const TH = {
  padding: '12px 16px',
  textAlign: 'left',
  fontWeight: 700,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#475569',
  backgroundColor: 'rgba(248, 250, 252, 0.5)',
  whiteSpace: 'nowrap',
};
export const TH_CENTER = { ...TH, textAlign: 'center' };

// Body cell
export const TD = { padding: '12px 16px', fontSize: '0.875rem', color: '#1e293b' };
export const TD_CENTER = { ...TD, textAlign: 'center' };

// Avatar shown in the Name column
export const AVATAR_SX = { width: 28, height: 28, bgcolor: 'primary.main', fontSize: '10px' };

// Chip used for IDs / serial numbers
export const CHIP_SX = { fontWeight: 700, borderRadius: '6px' };

// One body row — CSS-only hover, no per-row JS handlers.
export const Row = styled('tr')(({ theme }) => ({
  transition: 'background-color 0.15s ease',
  borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
}));

// Rounded, bordered, horizontally-scrollable wrapper for the <table>.
export const TableWrap = styled('div')({
  overflowX: 'auto',
  borderRadius: '16px',
  border: '1px solid rgba(226, 232, 240, 0.5)',
});

// Spread onto the <table> element itself.
export const TABLE_STYLE = { width: '100%', borderCollapse: 'collapse' };

// ---------------------------------------------------------------------------
// MUI <Table>/<TableCell> equivalents — use these (as `sx`) on tables built
// from MUI components so they match the raw-HTML tables above pixel-for-pixel.
// ---------------------------------------------------------------------------

// Header <TableCell sx={HEAD_CELL_SX}>
export const HEAD_CELL_SX = {
  fontWeight: 700,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#475569',
  backgroundColor: 'rgba(248, 250, 252, 0.5)',
  borderBottom: 'none',
  whiteSpace: 'nowrap',
};

// Body <TableCell sx={BODY_CELL_SX}>
export const BODY_CELL_SX = { fontSize: '0.875rem', color: '#1e293b' };

// Body <TableRow sx={TABLE_ROW_SX}> — CSS-only hover + soft row divider.
export const TABLE_ROW_SX = {
  transition: 'background-color 0.15s ease',
  '&:hover': { backgroundColor: (t) => alpha(t.palette.primary.main, 0.04) },
  '& td, & th': { borderBottom: '1px solid rgba(226, 232, 240, 0.5)' },
};

// Wrapping <TableContainer component={Paper} sx={TABLE_PAPER_SX}> / Paper.
export const TABLE_PAPER_SX = {
  borderRadius: '16px',
  border: '1px solid rgba(226, 232, 240, 0.5)',
  boxShadow: 'none',
  // Horizontal scrollbar when the table is wider than its container (many
  // columns at 100% zoom on smaller laptops) instead of silently clipping the
  // trailing columns. overflow-y computes to `auto` too, so the border-radius
  // still clips the corners cleanly.
  overflowX: 'auto',
};
